import { useMemo } from 'react';
import { VirtualSelectList } from 'components/select/VirtualSelectList';
import { SelectCheckbox } from 'components/select/SelectCheckbox';
import { IconLabel } from './IconLabel';
import type { TreeItemWithIcon } from './types';

interface TreeSelectContentProps {
  label: string;
  items: TreeItemWithIcon[];
  selectedItems: Set<string>;
  expandedItems: Set<string>;
  search: string;
  onToggleExpanded: (id: string) => void;
  onToggleSelected: (id: string) => void;
}

interface TreeRow {
  item: TreeItemWithIcon;
  level: number;
  parentId?: string;
  position: number;
  siblingCount: number;
  indeterminate: boolean;
}

export function TreeSelectContent({
  label,
  items,
  selectedItems,
  expandedItems,
  search,
  onToggleExpanded,
  onToggleSelected,
}: TreeSelectContentProps) {
  const isSearching = search.trim().length > 0;
  const { rows, effectiveExpandedItems } = useMemo(() => {
    const result: TreeRow[] = [];
    const query = search.trim().toLowerCase();
    const matching = new Set<string>();
    const partial = new Set<string>();
    const forcedExpanded = new Set<string>();
    const visit = (item: TreeItemWithIcon): { matches: boolean; selected: boolean; containsLabelMatch: boolean } => {
      const children = (item.children ?? []).map(visit);
      const labelMatches = query.length > 0 && item.label.toLowerCase().includes(query);
      const containsLabelMatch = labelMatches || children.some(child => child.containsLabelMatch);
      const matches = !query || labelMatches || children.some(child => child.matches);
      const selected = selectedItems.has(item.id) || children.some(child => child.selected);
      if (matches) matching.add(item.id);
      if (!selectedItems.has(item.id) && children.some(child => child.selected)) partial.add(item.id);
      if (query && children.some(child => child.containsLabelMatch)) forcedExpanded.add(item.id);
      return { matches, selected, containsLabelMatch };
    };
    items.forEach(visit);
    const effectiveExpandedItems = new Set(expandedItems);
    forcedExpanded.forEach(id => effectiveExpandedItems.add(id));
    const flatten = (siblings: TreeItemWithIcon[], level: number, parentId?: string) => {
      const visible = siblings.filter(item => matching.has(item.id));
      visible.forEach((item, index) => {
        result.push({
          item,
          level,
          parentId,
          position: index + 1,
          siblingCount: visible.length,
          indeterminate: partial.has(item.id),
        });
        if (effectiveExpandedItems.has(item.id)) flatten(item.children ?? [], level + 1, item.id);
      });
    };
    flatten(items, 1);
    return { rows: result, effectiveExpandedItems };
  }, [items, selectedItems, expandedItems, search]);

  return (
    <div role="tree" aria-label={label} aria-multiselectable="true" className="flex min-h-0 flex-col">
      <VirtualSelectList
        items={rows}
        getKey={row => row.item.id}
        getLabel={row => row.item.label}
        resetKey={search}
        renderItem={({ item, level, parentId, position, siblingCount, indeterminate }, index, tabIndex, focusIndex) => {
          const branch = Boolean(item.children?.length);
          const expanded = effectiveExpandedItems.has(item.id);
          const selected = selectedItems.has(item.id);
          return (
            <div
              role="treeitem"
              aria-label={item.label}
              aria-level={level}
              aria-posinset={position}
              aria-setsize={siblingCount}
              aria-expanded={branch ? expanded : undefined}
              aria-selected={selected}
              aria-checked={indeterminate ? 'mixed' : selected}
              tabIndex={tabIndex}
              data-virtual-focus
              data-testid={`tree-select-item-${item.id}`}
              className="flex h-12 cursor-pointer items-center gap-1 pr-3 text-sm hover:bg-snow-100 aria-selected:bg-snow-200"
              style={{ paddingLeft: 8 + (level - 1) * 24 }}
              onClick={() => {
                if (branch) {
                  if (!isSearching) onToggleExpanded(item.id);
                } else onToggleSelected(item.id);
              }}
              onKeyDown={event => {
                if (event.key === ' ' || event.key === 'Enter') {
                  event.preventDefault();
                  if (event.key === 'Enter' && branch) {
                    if (!isSearching) onToggleExpanded(item.id);
                  } else onToggleSelected(item.id);
                } else if (event.key === 'ArrowRight') {
                  event.preventDefault();
                  if (branch && !expanded && !isSearching) onToggleExpanded(item.id);
                  else if (rows[index + 1]?.parentId === item.id) focusIndex(index + 1);
                } else if (event.key === 'ArrowLeft') {
                  event.preventDefault();
                  if (branch && expanded && !isSearching) onToggleExpanded(item.id);
                  else if (parentId) focusIndex(rows.findIndex(row => row.item.id === parentId));
                }
              }}
            >
              <span aria-hidden="true" className="flex w-4 shrink-0 justify-center">
                {branch ? (expanded ? '▾' : '▸') : null}
              </span>
              <span
                onClick={event => {
                  event.stopPropagation();
                  onToggleSelected(item.id);
                }}
              >
                <SelectCheckbox checked={selected} indeterminate={indeterminate} />
              </span>
              <IconLabel icon={item.icon}>{item.label}</IconLabel>
            </div>
          );
        }}
      />
    </div>
  );
}
